import { hashKey, type QueryFunction } from '@tanstack/react-query';
import { trackDistribution, trackMessage } from '#api/errors.ts';
import type { Metadata, ResourceListOptions } from '#dn-types/api.ts';

/** Hashed query keys whose size was already recorded, so refetches don't re-record for the rest of the session. */
const reportedSizes = new Set<string>();
/** Hashed query keys already reported as truncated, so refetches don't re-report for the rest of the session. */
const reportedTruncations = new Set<string>();

/** Forgets reported sizes and truncations; call wherever the query cache is wiped, since the next session may be a different org. */
export function clearMaxListReports() {
  reportedSizes.clear();
  reportedTruncations.clear();
}

/** The filters in a `list()` query key, which spreads them in as "0", "1", … beside `cursor` and `pageSize`. */
function listKeyFilters(params: unknown) {
  if (typeof params !== 'object' || params === null) {
    return [];
  }
  return Object.entries(params)
    .filter(([key]) => /^\d+$/.test(key))
    .map(([, value]) => value as unknown);
}

type PaginatedResourceQueryFn<T> = (options: ResourceListOptions) => Promise<{ data: T[]; metadata: Metadata }>;

/**
 * Creates a query function for react-query which will:
 *
 * - fetch a 500-item page from a list endpoint,
 * - check whether there are more rows,
 * - fetch the next page and check again
 * - repeat
 *
 * It does this until it has fetched all results or has made maxPages (default 10) pages of requests.
 * Hitting maxPages silently drops the remaining rows from every consumer, so it's reported to
 * Sentry, once per query key per session, as the signal that the account has outgrown
 * fetch-everything for this resource. Every list's size is also recorded, once per query key per
 * session, in the `max_list.fetched_count` distribution, so growth toward the cap shows up first.
 */
export function makeMaxResourceQueryFn<T>(
  queryFn: PaginatedResourceQueryFn<T>,
  maxPages = 10
): QueryFunction<{ data: T[]; metadata: Metadata }> {
  return async ({ signal, queryKey }) => {
    let allResources: T[] = [];

    let currentResponse: { data: T[]; metadata: Metadata } | undefined;
    let currentPage = 1;
    do {
      // Fetch resource at least once, append to `allResources`, then continue if we have more pages.
      currentResponse = await queryFn({
        cursor: currentResponse?.metadata.nextCursor,
        pageSize: 500,
        init: { signal },
      });
      allResources = allResources.concat(currentResponse.data);
      currentPage++;
    } while (currentResponse.metadata.hasNextPage && currentPage <= maxPages);

    const reportKey = hashKey(queryKey);
    if (!reportedSizes.has(reportKey)) {
      reportedSizes.add(reportKey);
      trackDistribution('max_list.fetched_count', allResources.length, {
        attributes: { resource: String(queryKey[0]) },
      });
    }
    if (currentResponse.metadata.hasNextPage && !reportedTruncations.has(reportKey)) {
      reportedTruncations.add(reportKey);
      trackMessage('Max resource query hit its page limit; results are truncated', {
        level: 'error',
        // Query keys are readable where function names are minified, and group events per resource.
        fingerprint: ['max-resource-query-truncated', String(queryKey[0])],
        tags: { queryKey: String(queryKey[0]) },
        extra: { maxPages, fetchedCount: allResources.length, filters: listKeyFilters(queryKey[2]) },
      });
    }

    return {
      data: allResources,
      metadata: {
        hasNextPage: false, // TODO: this could be true if we hit the maxPages limit
        hasPrevPage: false,
        totalCount: allResources.length,
        nextCursor: '',
        prevCursor: '',
      } satisfies Metadata,
    };
  };
}
